import { Button, ButtonProps, IconButton } from "@chakra-ui/button";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DeleteIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { Box, Divider, Flex, Heading, Text, VStack } from "@chakra-ui/layout";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";
import { useColorModeValue } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import useConfirmation from "../../hooks/useConfirmation";
import useGetUserRole from "../../hooks/useGetUserRole";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { PdfGenerationActions } from "../../lib/types/PDFGenerationActions";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { isElevatedUser } from "../../lib/UserPermissions";
import DeleteQuoteDialog from "../DeleteQuoteDialog";
import { Calculator } from "../icons/Calculator";
import DocumentReportIcon from "../icons/DocumentReportIcon";
import DotsHorizontalIcon from "../icons/DotsHorizontalIcon";
import ShowForRole from "../ShowForRole";

interface FormQuoteActionsProps {
  handler: (action: QuoteFormActions, values: any) => void;
  exportHandler: (action: PdfGenerationActions, values: any) => void;
  generatingPdf?: boolean;
  calculating?: boolean;
}

function FormQuoteActions(props: FormQuoteActionsProps) {
  const { handler, exportHandler, generatingPdf, calculating } = props;
  const quoteContext = useQuote();
  const userRole = useGetUserRole();
  const { values, isValid, dirty } = useFormikContext<any>();
  const { state, isOwned, fetchQuote } = quoteContext;
  const { role } = useUser();
  const [isOpen, setIsOpen] = React.useState(true);
  const buttonText = useColorModeValue("gray.800", "white");
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);

  const handleGeneratePdfClick = (action: PdfGenerationActions) => {
    exportHandler(action, values);
  };

  const quoteHandler = (action: QuoteFormActions) => {
    handler(action, values);
  };

  const { dialogOpen, loading, toggleOpen, performAction } = useConfirmation(
    () => quoteHandler("DELETE")
  );

  const commonDisable =
    !isValid ||
    !state.hasCalculated ||
    state.hasCalculationErrors ||
    state.requiresQuoteResubmission ||
    state.hasFormErrors;

  return (
    <Box background={bgColor} padding={4} borderRadius="md" boxShadow="sm">
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="sm">Quote Actions</Heading>
        <Button
          display={{ base: "inline-flex", lg: "none" }}
          rightIcon={
            isOpen ? (
              <ChevronUpIcon height={6} width={6} />
            ) : (
              <ChevronDownIcon height={6} width={6} />
            )
          }
          onClick={() => setIsOpen(!isOpen)}
          variant="unstyled"
          size="sm"
        >
          {isOpen ? "Collapse" : "Expand"}
        </Button>
      </Flex>

      {isOpen && (
        <>
          <Divider paddingBottom={3} />
          <VStack spacing={6} alignItems="flex-start" marginTop={4}>
            <Button
              color={buttonText}
              leftIcon={<Calculator opacity={0.7} h={6} w={6} />}
              onClick={() => quoteHandler("CALCULATE")}
              isDisabled={!isValid || state.hasFormErrors}
              isLoading={calculating}
              loadingText="Calculating.."
              {...QuoteActionButtonStyle}
            >
              <Text fontSize="sm" flex="1">
                Calculate Quote
              </Text>
            </Button>

            <Flex width="full" justifyContent="space-between">
              <Button
                color={buttonText}
                leftIcon={<DocumentReportIcon opacity={0.7} h={6} w={6} />}
                isLoading={generatingPdf}
                onClick={() => {
                  userRole !== "External"
                    ? handleGeneratePdfClick("CUSTOMER")
                    : handleGeneratePdfClick("WHITELABEL");
                }}
                loadingText="Generating.."
                isDisabled={commonDisable || state.isNewQuote}
                {...QuoteActionButtonStyle}
              >
                <Text flex="1">Generate PDF</Text>
              </Button>

              <ShowForRole
                roles={[
                  "Sales",
                  "Broker",
                  "Broker Manager",
                  "Lender Finance",
                  "Lender Finance Manager",
                  "Capital Markets",
                  "Wholesale",
                  "Wholesale Manager",
                  "Inside Sales",
                  "Inside Sales Manager",
                  "Business Development",
                  "Business Development Manager",
                  "POD",
                  "POD Manager",
                  "Production Coordinator",
                  "Operations Analyst",
                  "Sales Assistant",
                  "Administrator",
                ]}
              >
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    title="More PDF Generation Options"
                    variant="link"
                    aria-label="More PDF Generation Options"
                    icon={<DotsHorizontalIcon h={6} w={6} />}
                    isDisabled={commonDisable || state.isNewQuote}
                  />

                  <MenuList>
                    <MenuItem
                      onClick={() => handleGeneratePdfClick("CUSTOMER")}
                    >
                      Customer PDF
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleGeneratePdfClick("INTERNAL")}
                    >
                      Internal PDF
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleGeneratePdfClick("WHITELABEL")}
                    >
                      Whitelabel PDF
                    </MenuItem>
                  </MenuList>
                </Menu>
              </ShowForRole>
            </Flex>

            <Button
              color={buttonText}
              leftIcon={<RepeatIcon opacity={0.7} h={5} w={6} />}
              onClick={() => fetchQuote()}
              isDisabled={commonDisable || dirty}
              {...QuoteActionButtonStyle}
            >
              <Text fontSize="sm" flex="1">
                Reload Quote
              </Text>
            </Button>

            <Button
              color={buttonText}
              leftIcon={<CopyIcon opacity={0.7} h={5} w={6} />}
              onClick={() => quoteHandler("SAVE_AS")}
              isDisabled={commonDisable || dirty}
              {...QuoteActionButtonStyle}
            >
              <Text fontSize="sm" flex="1">
                Copy Quote
              </Text>
            </Button>

            {(isOwned || isElevatedUser(role)) && (
              <>
                <Button
                  leftIcon={
                    <DeleteIcon opacity={0.7} h={5} w={6} color={"red.600"} />
                  }
                  onClick={() => toggleOpen()}
                  isDisabled={commonDisable || dirty}
                  {...QuoteActionButtonStyle}
                  _hover={{ color: "red.600", textDecoration: "underline" }}
                >
                  <Text fontSize="sm" flex="1" textColor={"red.600"}>
                    Delete Quote
                  </Text>
                </Button>
                <DeleteQuoteDialog
                  isOpen={dialogOpen}
                  isQuoteDeleting={loading}
                  onConfirmHandler={performAction}
                  onCloseHandler={toggleOpen}
                  quoteToDelete={null}
                />
              </>
            )}

            <Button
              fontSize="sm"
              w="full"
              isDisabled={
                commonDisable || (!isOwned && !isElevatedUser(userRole))
              }
              variant="brand"
              onClick={() => quoteHandler("SAVE")}
            >
              Save Quote
            </Button>
          </VStack>
        </>
      )}
    </Box>
  );
}

const QuoteActionButtonStyle: ButtonProps = {
  fontSize: "sm",
  iconSpacing: 4,
  textAlign: "left",
  variant: "link",
};

export default FormQuoteActions;
